/**
 * Manage passkeys — list your devices, add one, remove a lost one (V072, PR 3).
 *
 * "Add a device" mints the same short-lived single-use grant the emailed
 * recovery flow issues, but skips the inbox: the user is already signed in, so
 * their identity is proven. It is shown as BOTH a copyable link and a QR code
 * — the phone you want to add is usually in your other hand, and scanning is
 * far less error-prone than retyping a token.
 *
 * The QR is rendered client-side (see QrCode) and never leaves the browser:
 * what it encodes is a live credential-adding grant, so handing it to an image
 * service to draw would be handing away the grant.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  passkeyService,
  type DeviceLinkMint,
  type PasskeyCredential,
} from "@re-platform/api-client";

import { api } from "../../lib/api";
import { QrCode } from "../auth/QrCode";

function describe(cred: PasskeyCredential): string {
  if (cred.friendly_name) return cred.friendly_name;
  // Transports are the only other hint at what the thing physically is.
  if (cred.transports?.includes("internal")) return "This kind of device";
  if (cred.transports?.includes("usb")) return "Security key";
  return "Passkey";
}

export function ManagePasskeys() {
  const qc = useQueryClient();
  const [mint, setMint] = useState<DeviceLinkMint | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const credsQ = useQuery({
    queryKey: ["passkey-credentials"],
    queryFn: () => passkeyService.listCredentials(api),
    retry: false,
  });

  const addDevice = useMutation({
    mutationFn: () => passkeyService.mintDeviceLink(api),
    onSuccess: (data) => {
      setError(null);
      setCopied(false);
      setMint(data);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : "Couldn't create a link."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => passkeyService.deleteCredential(api, id),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["passkey-credentials"] });
    },
    // The server refuses the LAST credential (409) — surface its wording
    // rather than inventing our own.
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : "Couldn't remove that."),
  });

  const creds = credsQ.data ?? [];

  return (
    <div
      style={{
        marginTop: 16,
        background: "var(--rp-color-surface, white)",
        border: "1px solid var(--rp-color-border, #e2e8f0)",
        borderRadius: 12,
        padding: 24,
      }}
    >
      <h2 style={{ margin: "0 0 4px", fontSize: "1.05rem" }}>Your devices</h2>
      <p
        style={{
          margin: "0 0 16px",
          fontSize: "0.85rem",
          color: "var(--rp-color-text-muted)",
        }}
      >
        Each device you sign in with has its own passkey. Add your phone so you
        can still get in if you lose your computer — and vice versa.
      </p>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: "10px 14px",
            background: "var(--rp-color-danger-bg)",
            border: "1px solid var(--rp-color-danger)",
            borderRadius: 8,
            color: "var(--rp-color-danger)",
            fontSize: "0.85rem",
          }}
        >
          {error}
        </div>
      )}

      {credsQ.isLoading ? (
        <p style={{ fontSize: "0.9rem" }}>Loading…</p>
      ) : (
        <div style={{ marginBottom: 16 }}>
          {creds.map((cred) => (
            <div
              key={cred.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "10px 0",
                borderBottom: "1px solid var(--rp-color-border, #e2e8f0)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.92rem" }}>
                  {describe(cred)}
                </div>
                <div
                  style={{
                    fontSize: "0.78rem",
                    color: "var(--rp-color-text-muted)",
                  }}
                >
                  Added {cred.created_at.slice(0, 10)}
                  {cred.last_used_at
                    ? ` · last used ${cred.last_used_at.slice(0, 10)}`
                    : " · not used yet"}
                </div>
              </div>
              <button
                type="button"
                className="rp-btn rp-btn--secondary rp-btn--sm"
                // Removing your only passkey would lock you out permanently,
                // with no recovery a support agent could run. The server
                // enforces this too — this just avoids offering the trap.
                disabled={creds.length <= 1 || remove.isPending}
                title={
                  creds.length <= 1
                    ? "Add another device before removing this one"
                    : "Remove this device"
                }
                onClick={() => remove.mutate(cred.id)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {mint ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            padding: 16,
            background: "var(--rp-color-surface-2, #f8fafc)",
            borderRadius: 10,
          }}
        >
          <p style={{ margin: 0, fontSize: "0.88rem", textAlign: "center" }}>
            Scan this with the device you want to add, or open the link on it.
          </p>
          <QrCode
            value={mint.add_device_url}
            size={200}
            label="QR code linking to the add-a-device page"
          />
          <code
            style={{
              fontSize: "0.72rem",
              wordBreak: "break-all",
              textAlign: "center",
            }}
          >
            {mint.add_device_url}
          </code>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="rp-btn rp-btn--secondary rp-btn--sm"
              onClick={() => {
                void navigator.clipboard?.writeText(mint.add_device_url);
                setCopied(true);
              }}
            >
              {copied ? "Copied ✓" : "Copy link"}
            </button>
            <button
              type="button"
              className="rp-btn rp-btn--secondary rp-btn--sm"
              onClick={() => setMint(null)}
            >
              Done
            </button>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.78rem",
              color: "var(--rp-color-text-muted)",
            }}
          >
            Works once, and expires in {Math.round(mint.expires_in / 60)}{" "}
            minutes.
          </p>
        </div>
      ) : (
        <button
          type="button"
          className="rp-btn rp-btn--primary rp-btn--sm"
          disabled={addDevice.isPending}
          onClick={() => addDevice.mutate()}
        >
          {addDevice.isPending ? "Preparing…" : "Add a device"}
        </button>
      )}
    </div>
  );
}
